import { useState } from "react";
import { useQuery } from "@tanstack/react-query";

import { JsonView } from "@/components/json-view";
import { Panel } from "@/components/panel";
import { QueryErrorState } from "@/components/query-error-state";
import { Button } from "@/components/ui/button";
import { apiGet } from "@/lib/api-client";
import { formatRelativeTime } from "@/lib/format";
import { asArray, asRecord } from "@/lib/type-utils";

export function ReportsPage() {
  const [generationNotice, setGenerationNotice] = useState<string | null>(null);

  const reportsQuery = useQuery({
    queryKey: ["reports", "list"],
    queryFn: () => apiGet<unknown>("/api/evaluation/reports"),
    refetchInterval: 60_000,
  });

  const reportPayload = asRecord(reportsQuery.data);
  // Backend rows carry only {id, type, created_at}; downloads are served as JSON.
  const generatedReports = asArray(reportPayload.reports ?? reportsQuery.data);
  const reportsUnavailable = reportsQuery.isError;

  // Rows come from an external API list — guard against null/non-object
  // elements before touching properties (Gemini, PR #1896).
  const distinctTypes = new Set(
    generatedReports.map((r) =>
      String((r && typeof r === "object" ? r.type : null) ?? "unknown"),
    ),
  );
  const newestReport = generatedReports.reduce<string | null>((newest, r) => {
    const created =
      r && typeof r === "object" && r.created_at != null
        ? String(r.created_at)
        : null;
    if (created == null) return newest;
    return newest == null || new Date(created).getTime() > new Date(newest).getTime()
      ? created
      : newest;
  }, null);

  const downloadReport = (id: string) => {
    window.open(`/api/evaluation/reports/${id}`, "_blank");
  };

  const requestReportGeneration = (typeName: string, formatName: string) => {
    setGenerationNotice(
      `${typeName} ${formatName} export is not connected to a report-generation backend yet. Existing generated reports remain available below.`,
    );
  };

  const reportTypes = [
    { id: "trading", name: "Trading Performance", description: "P&L, win rate, and trade analytics" },
    { id: "portfolio", name: "Portfolio Summary", description: "Positions, exposure, and allocation" },
    { id: "signals", name: "Signal Performance", description: "Signal accuracy and effectiveness" },
    { id: "costs", name: "Cost Analysis", description: "API spend and cost breakdown" },
  ];

  const formats = [
    { id: "pdf", name: "PDF", icon: "📄" },
    { id: "csv", name: "CSV", icon: "📊" },
    { id: "xlsx", name: "Excel", icon: "📈" },
  ];

  return (
    <div className="space-y-4 md:space-y-6">
      {reportsUnavailable ? (
        <QueryErrorState
          what="report list"
          onRetry={() => void reportsQuery.refetch()}
        />
      ) : null}

      <Panel
        title="Reports Center"
        description="Generate and download trading reports, analytics, and performance summaries."
      >
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          {[
            {
              label: "Available Reports",
              value: reportsUnavailable ? "—" : String(generatedReports.length),
            },
            {
              label: "Report Types",
              value: reportsUnavailable ? "—" : String(distinctTypes.size),
            },
            {
              label: "Newest Report",
              value: reportsUnavailable
                ? "—"
                : newestReport != null
                  ? formatRelativeTime(newestReport)
                  : "—",
            },
            { label: "Served As", value: "JSON" },
          ].map((stat, index) => (
            <div key={index} className="rounded-lg bg-gunmetal/50 p-3 text-center">
              <div className="text-xl font-bold text-gold-400">{stat.value}</div>
              <div className="text-xs text-slate-400">{stat.label}</div>
            </div>
          ))}
        </div>
      </Panel>

      <Panel title="Generate Reports" description="Create new trading and analytics reports">
        {generationNotice && (
          <div className="mb-4 rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-sm text-amber-300">
            {generationNotice}
          </div>
        )}
        <div className="space-y-4">
          {reportTypes.map((type) => (
            <div key={type.id} className="flex flex-col gap-3 rounded-lg bg-gunmetal/50 p-4 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <div className="font-medium">{type.name}</div>
                <div className="text-xs text-slate-400">{type.description}</div>
              </div>
              <div className="flex gap-2">
                {formats.map((format) => (
                  <Button
                    key={format.id}
                    variant="ghost"
                    className="flex items-center gap-1 text-sm"
                    onClick={() => requestReportGeneration(type.name, format.name)}
                    title="Report generation backend not connected yet"
                  >
                    <span>{format.icon}</span>
                    {format.name}
                  </Button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </Panel>

      <Panel title="Generated Reports" description="Previously generated report downloads (served as JSON)">
        <div className="max-h-[400px] space-y-2 overflow-y-auto">
          {reportsUnavailable ? (
            <div className="rounded-lg bg-gunmetal/50 p-4 text-center text-sm text-amber-300">
              Report list unavailable — backend unreachable
            </div>
          ) : generatedReports.length === 0 ? (
            <div className="rounded-lg bg-gunmetal/50 p-4 text-center text-sm text-slate-400">
              No reports generated yet. Use the panel above to create reports.
            </div>
          ) : (
            generatedReports
              .sort((a, b) => new Date(String(b.created_at ?? b.date)).getTime() - new Date(String(a.created_at ?? a.date)).getTime())
              .map((report, index) => (
                <div key={index} className="flex items-center justify-between rounded-lg bg-gunmetal/50 p-3">
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">📄</span>
                    <div>
                      <div className="text-sm font-medium capitalize">
                        {String(report.type ?? "Untitled Report").replace(/_/g, " ")}
                      </div>
                      <div className="text-xs text-slate-400">
                        {formatRelativeTime(report.created_at ?? report.date)} • JSON
                      </div>
                    </div>
                  </div>
                  <Button
                    className="text-sm"
                    onClick={() => downloadReport(String(report.id))}
                  >
                    Download JSON
                  </Button>
                </div>
              ))
          )}
        </div>
      </Panel>

      <Panel title="Scheduled Reports" description="Configure automatic report generation">
        <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-4 text-sm text-amber-300">
          Report scheduling is not configured yet — there is no scheduler backend
          connected, so no reports are generated automatically. This panel will list
          real schedules once a scheduler exists.
        </div>
      </Panel>

      <Panel title="Reports API" description="Raw report data">
        <JsonView value={reportsQuery.data} />
      </Panel>
    </div>
  );
}
